import React, { useCallback, useEffect, useState } from 'react';
import { INotification } from 'stores/networkInterfaces';
import { INotificationQuery } from 'stores/NotificationStore';
import { useStore } from 'stores/store';
import { isTextSelection } from 'utils/domUtils';
import { useUserAuth } from 'utils/hooks/useUserAuth';

export const useDirectNotificationApi = ({ unread }: { unread?: boolean }) => {
  const store = useStore();
  const { isAuthenticated } = useUserAuth();

  const [query, setQuery] = useState<INotificationQuery>({
    limit: 10,
    offset: 0,
    filter: unread ? 'unread' : undefined
  });

  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (isAuthenticated) {
      if (isLoading) {
        store.NotificationStore.listDirectNotifications(query)
          .catch((e) => {
            console.log('Failed to fetch direct notifications', e);
          })
          .finally(() => {
            setIsLoading(false);
          });
      }
    }
  }, [isAuthenticated, isLoading, query, store.NotificationStore]);

  const setUnread = useCallback(
    (unread?: boolean) => {
      const newFilter = unread ? 'unread' : undefined;
      if (newFilter != query?.filter) {
        setQuery({
          limit: 10,
          offset: 0,
          filter: newFilter
        });
        setIsLoading(true);
      }
    },
    [query?.filter]
  );

  const onLoadMore = useCallback(() => {
    setQuery({
      limit: 10,
      offset: unread
        ? Math.min(store.NotificationStore?.unreadDirectCount, (query?.offset || 0) + 10)
        : Math.min(store.NotificationStore?.totalDirectCount, (query?.offset || 0) + 10),
      filter: query?.filter
    });
    setIsLoading(true);
  }, [
    query?.filter,
    query?.offset,
    store.NotificationStore?.totalDirectCount,
    store.NotificationStore?.unreadDirectCount,
    unread
  ]);

  const onRefresh = useCallback(() => {
    setQuery({
      limit: 10,
      offset: 0,
      filter: query?.filter
    });
    setIsLoading(true);
  }, [query?.filter]);

  const onToggleUnread = useCallback(
    (notification: INotification) => {
      if (!isTextSelection()) {
        if (notification.read_at) {
          store.NotificationStore.markDirectAsUnread(notification);
        } else {
          store.NotificationStore.markDirectAsRead(notification);
        }
      }
    },
    [store.NotificationStore]
  );

  const onNavigate = useCallback(
    async (notification: INotification) => {
      if (!isTextSelection()) {
        store.NotificationStore.markDirectAsRead(notification);
        await store.NotificationStore.navigateToNotification(notification);
        store.UIStore.setNotificationsActive(false);
      }
    },
    [store.NotificationStore, store.UIStore]
  );

  const setReadAll = useCallback(async () => {
    await store.NotificationStore.markAllDirectAsRead();
  }, [store.NotificationStore]);

  return {
    isAuthenticated,
    isLoading,
    setUnread,
    onRefresh,
    onLoadMore,
    onToggleUnread,
    setReadAll,
    onNavigate
  };
};
